/**
 * ConfigManager - Centralized configuration management for TitusJS
 * Handles configuration validation, defaults, and persistence
 */
export default class ConfigManager {
	/** @type {Object} Default configuration values */
	static #DEFAULT_CONFIG = {
		contentSelector: null,
		autoShow: true,
		ui: {
			position: "bottom-right",
			enableHighlighting: true,
			enableBanner: false,
		},
		speech: {
			rate: 1.0,
			pitch: 1.0,
			volume: 1.0,
		},
		dom: {
			segmentSelectors: [
				"h1",
				"h2",
				"h3",
				"h4",
				"h5",
				"h6",
				"p",
				"li",
				"blockquote",
				"figcaption",
				"dd",
				"dt",
				"td",
				"th",
			],
			excludedElements: [
				"header",
				"footer",
				"nav",
				"aside",
				"script",
				"style",
				"noscript",
				"template",
			],
			respectAria: true,
		},
	};

	/** @type {Object} Current configuration */
	#config = {};

	/** @type {string} Local storage key for persisting configuration */
	#storageKey = "titusjs-config";

	/**
	 * Initialize ConfigManager with user configuration
	 * @param {Object} userConfig - User-provided configuration
	 */
	constructor(userConfig = {}) {
		this.#config = this.#mergeConfigs(
			ConfigManager.#DEFAULT_CONFIG,
			userConfig,
		);
		this.#loadPersistedConfig();
	}

	/**
	 * Get the complete configuration object
	 * @returns {Object} Current configuration
	 */
	getConfig() {
		return structuredClone(this.#config);
	}

	/**
	 * Get a specific configuration value
	 * @param {string} path - Dot-notation path to the config value (e.g., 'ui.position')
	 * @returns {*} The configuration value
	 */
	get(path) {
		return this.#getNestedValue(this.#config, path);
	}

	/**
	 * Set a configuration value
	 * @param {string} path - Dot-notation path to the config value
	 * @param {*} value - New value to set
	 */
	set(path, value) {
		this.#setNestedValue(this.#config, path, value);
		this.#persistConfig();
	}

	/**
	 * Update multiple configuration values
	 * @param {Object} updates - Object with configuration updates
	 */
	update(updates) {
		this.#config = this.#mergeConfigs(this.#config, updates);
		this.#persistConfig();
	}

	/**
	 * Reset configuration to defaults
	 */
	reset() {
		this.#config = structuredClone(ConfigManager.#DEFAULT_CONFIG);
		this.#clearPersistedConfig();
	}

	/**
	 * Validate configuration values
	 * @param {Object} config - Configuration to validate
	 * @returns {boolean} Whether configuration is valid
	 */
	static validateConfig(config) {
		// Validate speech settings
		if (config.speech) {
			const { rate, pitch, volume } = config.speech;
			if (
				rate !== undefined &&
				(typeof rate !== "number" || rate < 0.1 || rate > 10)
			) {
				return false;
			}
			if (
				pitch !== undefined &&
				(typeof pitch !== "number" || pitch < 0 || pitch > 2)
			) {
				return false;
			}
			if (
				volume !== undefined &&
				(typeof volume !== "number" || volume < 0 || volume > 1)
			) {
				return false;
			}
		}

		// Validate UI position
		if (config.ui?.position) {
			const validPositions = [
				"bottom-right",
				"bottom-left",
				"top-right",
				"top-left",
				"center",
			];
			if (!validPositions.includes(config.ui.position)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Deep merge two configuration objects
	 * @param {Object} target - Target configuration
	 * @param {Object} source - Source configuration to merge
	 * @returns {Object} Merged configuration
	 * @private
	 */
	#mergeConfigs(target, source) {
		const result = structuredClone(target);

		for (const key in source) {
			if (
				source[key] &&
				typeof source[key] === "object" &&
				!Array.isArray(source[key])
			) {
				result[key] = this.#mergeConfigs(
					result[key] || {},
					source[key],
				);
			} else {
				result[key] = source[key];
			}
		}

		return result;
	}

	/**
	 * Get nested value using dot notation
	 * @param {Object} obj - Object to search
	 * @param {string} path - Dot-notation path
	 * @returns {*} Found value or undefined
	 * @private
	 */
	#getNestedValue(obj, path) {
		return path.split(".").reduce((current, key) => current?.[key], obj);
	}

	/**
	 * Set nested value using dot notation
	 * @param {Object} obj - Object to modify
	 * @param {string} path - Dot-notation path
	 * @param {*} value - Value to set
	 * @private
	 */
	#setNestedValue(obj, path, value) {
		const keys = path.split(".");
		const lastKey = keys.pop();
		const target = keys.reduce((current, key) => {
			if (!(key in current)) current[key] = {};
			return current[key];
		}, obj);
		target[lastKey] = value;
	}

	/**
	 * Load persisted configuration from localStorage
	 * @private
	 */
	#loadPersistedConfig() {
		if (!this.#isLocalStorageAvailable()) return;

		try {
			const saved = localStorage.getItem(this.#storageKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				if (ConfigManager.validateConfig(parsed)) {
					this.#config = this.#mergeConfigs(this.#config, parsed);
				}
			}
		} catch (error) {
			console.warn("TitusJS: Could not load persisted config:", error);
		}
	}

	/**
	 * Persist current configuration to localStorage
	 * @private
	 */
	#persistConfig() {
		if (!this.#isLocalStorageAvailable()) return;

		try {
			localStorage.setItem(
				this.#storageKey,
				JSON.stringify(this.#config),
			);
		} catch (error) {
			console.warn("TitusJS: Could not persist config:", error);
		}
	}

	/**
	 * Clear persisted configuration
	 * @private
	 */
	#clearPersistedConfig() {
		if (!this.#isLocalStorageAvailable()) return;

		try {
			localStorage.removeItem(this.#storageKey);
		} catch (error) {
			console.warn("TitusJS: Could not clear persisted config:", error);
		}
	}

	/**
	 * Check if localStorage is available
	 * @returns {boolean} Whether localStorage is available
	 * @private
	 */
	#isLocalStorageAvailable() {
		try {
			const test = "__tjs_storage_test__";
			localStorage.setItem(test, test);
			localStorage.removeItem(test);
			return true;
		} catch {
			return false;
		}
	}
}
